import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, TextInput, Badge, Spinner, Alert } from '@opendate-app/setlist';
import { formatActivity } from '../../Deals/components/dealStages';

function plainText(note) {
  return (note.sanitized_body || note.body || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function DealNotesTimeline({
  notes = [],
  loading = false,
  error = '',
  onCreateNote,
  onEditNote,
  onDeleteNote,
  canManageNotes = false,
}) {
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [composerError, setComposerError] = useState('');

  const handleAdd = async () => {
    const value = body.trim();
    if (!value || !onCreateNote || submitting) return;
    setSubmitting(true);
    setComposerError('');
    try {
      await onCreateNote(value);
      setBody('');
    } catch (err) {
      setComposerError('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="deal-timeline-card">
      <div className="deal-timeline-header">
        <h2 className="deal-timeline-title">Activity</h2>
      </div>

      {canManageNotes && (
        <div className="deal-timeline-composer">
          <TextInput
            multiline
            rows={2}
            placeholder="Add a note…"
            value={body}
            disabled={submitting}
            onChange={(e) => { setBody(e.target.value); setComposerError(''); }}
          />
          {composerError && (
            <div style={{ marginTop: 8 }}>
              <Alert color="error" description={composerError} />
            </div>
          )}
          <div className="deal-timeline-composer-actions">
            <Button
              variant="primary"
              onClick={handleAdd}
              disabled={!body.trim() || submitting || !onCreateNote}
            >
              {submitting ? 'Adding…' : 'Add note'}
            </Button>
          </div>
        </div>
      )}

      {loading && (
        <div className="d-flex justify-content-center" style={{ padding: 16 }}>
          <Spinner label="Loading activity…" />
        </div>
      )}
      {!loading && error && (
        <div style={{ padding: '8px 0' }}>
          <Alert color="error" description={error} />
        </div>
      )}
      {!loading && !error && notes.length === 0 && (
        <div className="deal-timeline-empty">No activity yet.</div>
      )}

      {!loading && !error && notes.length > 0 && (
        <ol className="deal-timeline">
          {notes.map((note) => {
            const author = (note.team_membership && note.team_membership.name) || 'Unknown';
            return (
              <li key={note.id} className="deal-timeline-item">
                <span className="deal-timeline-dot" aria-hidden="true" />
                <div className="deal-timeline-body">
                  <div className="deal-timeline-meta">
                    <span className="deal-timeline-author">{author}</span>
                    <Badge size="sm" color="gray">Note</Badge>
                    <span className="deal-timeline-date">{note.human_created_at || formatActivity(note.created_at)}</span>
                    {canManageNotes && (
                      <span className="deal-timeline-actions">
                        {onEditNote && (
                          <Button variant="link-gray" size="sm" onClick={() => onEditNote(note)}>Edit</Button>
                        )}
                        {onDeleteNote && (
                          <Button variant="link-destructive" size="sm" onClick={() => onDeleteNote(note)}>Delete</Button>
                        )}
                      </span>
                    )}
                  </div>
                  <p className="deal-timeline-text">{plainText(note) || 'No content'}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

DealNotesTimeline.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  error: PropTypes.string,
  onCreateNote: PropTypes.func,
  onEditNote: PropTypes.func,
  onDeleteNote: PropTypes.func,
  canManageNotes: PropTypes.bool,
};

export default DealNotesTimeline;
